import { NgClass } from "@angular/common";
import { AfterViewInit, Component, EventEmitter, Input, OnDestroy, Output, QueryList, TemplateRef, ViewChild, ViewChildren, ChangeDetectionStrategy, inject } from "@angular/core";
import { NgbAccordionBody, NgbAccordionButton, NgbAccordionCollapse, NgbAccordionDirective, NgbAccordionHeader, NgbAccordionItem, NgbAccordionToggle, NgbCollapse } from "@ng-bootstrap/ng-bootstrap";
import { every } from "lodash";
import { CharTypeId } from "rl-common/rl-common.consts";
import { AccountingProcessesService } from "rl-common/services/accounting-processes/accounting-processes.service";
import { AccountingProcessActiveIndicator } from "rl-common/services/accounting-processes/models/accounting-process-active-indicator";
import { IAccountingOperation } from "rl-common/services/accounting-processes/models/i-accounting-operation";
import { IAccountingProcessAccount } from "rl-common/services/accounting-processes/models/i-accounting-process-account";
import { IAccountingProcessAccountGroup } from "rl-common/services/accounting-processes/models/i-accounting-process-account-group";
import { IAccountingProcessDetails } from "rl-common/services/accounting-processes/models/i-accounting-process-details";
import { ICreateAccountingOperation } from "rl-common/services/accounting-processes/models/i-create-accounting-operation";
import { OneConfigService } from "rl-common/services/one-config/one-config.service";
import { PromptService } from "rl-common/services/prompt/prompt.service";
import { Subscription } from "rxjs";
import { filter, switchMap, tap } from "rxjs/operators";
import { AccountingProcesses } from "../accounting-processes.consts";
import { FfAccountOperationComponent } from "../ff-account-operation/ff-account-operation.component";
import { FfBuildAccountingOperationComponent } from "../ff-build-accounting-operation/ff-build-accounting-operation.component";

@Component({
	selector: "rl-ff-accounting-operations",
	templateUrl: "./ff-accounting-operations.component.html",
	styleUrls: ["./ff-accounting-operations.component.scss"],
	changeDetection: ChangeDetectionStrategy.Eager,
	imports: [NgbAccordionDirective, NgbAccordionItem, NgbAccordionHeader, NgbAccordionToggle, NgbAccordionButton, NgClass, NgbCollapse, NgbAccordionCollapse, NgbAccordionBody, FfBuildAccountingOperationComponent, FfAccountOperationComponent]
})
export class FfAccountingOperationsComponent implements OnDestroy, AfterViewInit {
	private readonly _oneConfig = inject(OneConfigService);
	private readonly _accountingProcessService = inject(AccountingProcessesService);
	private readonly _promptService = inject(PromptService);


	@Input()
	accountingProcess: IAccountingProcessDetails;

	@Input()
	openPanelIds = [];

	@Input()
	editMode = false;

	@Input()
	accounts: IAccountingProcessAccount[] = [];

	@Input()
	accountGroups: IAccountingProcessAccountGroup[] = [];

	@Output()
	onEdit = new EventEmitter<IAccountingOperation>();

	@Output()
	onDelete = new EventEmitter<IAccountingOperation>();

	@Output()
	onSave = new EventEmitter<ICreateAccountingOperation>();

	get accountingOperations(): IAccountingOperation[] {
		return this.accountingProcess?.operations ?? [];
	}

	get isArchived() {
		return this.accountingProcess?.activeIndicator === AccountingProcessActiveIndicator.Archived;
	}

	get hasContractAdjustments() {
		return this.accountingProcess?.mostRecentlySent?.length > 0;
	}

	@ViewChild("accordion")
	accordion: NgbAccordionDirective;

	@ViewChildren("item")
	accordionItemsQueryList: QueryList<NgbAccordionItem>;

	accordionItems: NgbAccordionItem[] = [];

	get isAllCollapsed() {
		return every(this.accordionItems, item => item.collapsed);
	}

	deletingOperations: Set<string> = new Set<string>();
	private readonly _subs: Subscription[] = [];

	ngAfterViewInit(): void {
		this.accordionItems = this.accordionItemsQueryList.toArray();
	}

	operationSaved(event: ICreateAccountingOperation) {
		const operation = this.accountingProcess.operations.find(x => x.id == event.id);
		operation.operationName = event.operationName;
		operation.anchorTemplateId = event.anchorTemplateId;
		operation.costAnchorTemplateId = event.costAnchorTemplateId;
		this.onSave.emit(event);
	}

	editOperation(operation: IAccountingOperation) {
		this.onEdit.emit(operation);
	}

	deleteOperation(deleteProcessPrompt: TemplateRef<unknown>, operation: IAccountingOperation) {
		const sub = this._promptService.confirmMini("Delete Accounting Operation", deleteProcessPrompt, { operation }).pipe(
			filter(confirm => confirm),
			tap(() => this.deletingOperations.add(operation.id)),
			switchMap(() => this._accountingProcessService.deleteAccountingOperation(operation.id))
		).subscribe(() => {
			this.onDelete.emit(operation);
		});
		this._subs.push(sub);
	}

	ngOnDestroy(): void {
		this._subs.forEach(sub => sub.unsubscribe());
	}

	getTemplateName(templateId: number) {
		return this._oneConfig.getTemplate(CharTypeId.Amount, templateId)?.templateName;
	}

	getOperationType(id: number) {
		return AccountingProcesses.operationTypes.find(o => o.id === id)?.name;
	}
}
